import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { Lock as LockIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { Avatar } from "@mui/material";
import { formatDate } from "../../lib/formatDate";
import { Employee } from "../../types";
import { NAVIGATION, ROLES } from "../../constants";
import { useEmployees } from "../../hooks/useEmployees";
import { useNavigationState } from "../../hooks/useNavigationState";
import DataTable from "../../components/tables/DataTable";
import FilesColumn from "../../components/files/FilesColumn";
import PasswordChangeDialog from "../../components/PasswordChangeDialog";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { useAuth } from "../../contexts/AuthContext";

const Employees: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state: navState, updateState } = useNavigationState();
  const [tabValue, setTabValue] = useState(navState.tab || 0);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );

  const isAdmin = user?.role === ROLES.ADMIN;

  // Restore navigation state when component mounts
  useEffect(() => {
    if (navState.tab !== undefined) {
      setTabValue(navState.tab);
    }
  }, [navState.tab]);

  const { employees, isLoading, error, deleteEmployee, isDeleting } =
    useEmployees();

  const activeEmployees = employees.filter((emp: Employee) => emp.isActive);
  const inactiveEmployees = employees.filter((emp: Employee) => !emp.isActive);
  const currentEmployees = tabValue === 0 ? activeEmployees : inactiveEmployees;

  const getColumns = (isInactive: boolean): GridColDef<Employee>[] => {
    const baseColumns: GridColDef<Employee>[] = [
      {
        field: "id",
        headerName: "#",
        width: 15,
        filterable: false,
        sortable: true,
        valueFormatter: (value: number, row: Employee) =>
          value ? currentEmployees.indexOf(row) + 1 : 0,
      },
      {
        field: "profilePicture",
        headerName: "Photo",
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Avatar
              src={params.row.user?.profilePicture || params.row.profilePicture}
              sx={{ width: 32, height: 32 }}
            >
              {params.row.firstName?.[0]}
              {params.row.lastName?.[0]}
            </Avatar>
          </Box>
        ),
      },
      {
        field: "lastName",
        headerName: "Last Name",
        flex: 1,
        filterable: true,
        sortable: true,
      },
      {
        field: "firstName",
        headerName: "First Name",
        flex: 1,
        filterable: true,
        sortable: true,
      },
      {
        field: "position",
        headerName: "Position",
        flex: 1,
        filterable: true,
        sortable: true,
      },
      {
        field: "birthDate",
        headerName: "Birth Date",
        flex: 1,
        filterable: true,
        sortable: true,
        valueFormatter: (value: any) => (value ? formatDate(value) : ""),
      },
      {
        field: "files",
        headerName: "Files",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <FilesColumn entityType="EMPLOYEE" entityId={params.row.id} />
        ),
      },
    ];

    // Add actions column for admins
    if (isAdmin) {
      baseColumns.push({
        display: "flex",
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Change Password">
              <IconButton
                size="small"
                onClick={(event: any) => {
                  event.stopPropagation();
                  setSelectedEmployee(params.row);
                  setPasswordDialogOpen(true);
                }}
              >
                <LockIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Employee">
              <IconButton
                size="small"
                color="error"
                onClick={(event: any) => {
                  event.stopPropagation();
                  setSelectedEmployee(params.row);
                  setDeleteDialogOpen(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      });
    }

    // Add termination date column only for inactive employees
    if (isInactive) {
      baseColumns.splice(6, 0, {
        field: "terminationDate",
        headerName: "Termination Date",
        flex: 1,
        filterable: true,
        sortable: true,
        valueFormatter: (value: any) => (value ? formatDate(value) : ""),
      });
    }

    return baseColumns;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    updateState({ tab: newValue, page: 0 }); // Reset page when changing tabs
  };

  const handleDeleteEmployee = () => {
    if (selectedEmployee) {
      deleteEmployee(selectedEmployee.id);
      setDeleteDialogOpen(false);
      setSelectedEmployee(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedEmployee(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Employees
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(NAVIGATION.ADD_EMPLOYEE)}
        >
          Add Employee
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label={`Active Employees (${activeEmployees.length})`} />
        <Tab label={`Inactive Employees (${inactiveEmployees.length})`} />
      </Tabs>

      {error instanceof Error && (
        <Typography color="error">{error.message}</Typography>
      )}

      <DataTable
        rows={currentEmployees}
        columns={getColumns(tabValue === 1)}
        loading={isLoading}
        navigationPath={NAVIGATION.EMPLOYEES}
        initialPage={navState.page}
        onPageChange={(page) => updateState({ page })}
      />

      <PasswordChangeDialog
        open={passwordDialogOpen}
        onClose={() => {
          setPasswordDialogOpen(false);
          setSelectedEmployee(null);
        }}
        isAdmin={true}
        targetUserId={selectedEmployee?.user?.id}
        targetUserName={
          selectedEmployee
            ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
            : ""
        }
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteEmployee}
        title="Delete Employee"
        itemName={
          selectedEmployee
            ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
            : ""
        }
        itemType="employee"
        loading={isDeleting}
      />
    </Box>
  );
};

export default Employees;
